import { useState } from 'react';
import { X } from 'lucide-react';
import type { MerchItem } from '@/domain/types';

const inputCls =
  'mt-1 block w-full bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream';
const labelCls = 'block text-[10px] font-inter uppercase tracking-widest text-cream-soft';

interface Props {
  // A full MerchItem draft: either an existing item (edit) or a freshly-built
  // blank with its id already assigned (add). The parent owns id generation so
  // the `merch-<studio>-<n>` keying stays in one place.
  item: MerchItem;
  mode: 'add' | 'edit';
  onCancel: () => void;
  onSave: (item: MerchItem) => void;
}

export default function MerchItemModal({ item, mode, onCancel, onSave }: Props) {
  const [draft, setDraft] = useState<MerchItem>(item);
  // Price is edited as a string so the field can go momentarily empty without
  // snapping to 0; parsed back to a number on save.
  const [priceText, setPriceText] = useState<string>(String(item.priceAud));

  const set = <K extends keyof MerchItem>(key: K, value: MerchItem[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const price = Number(priceText);
  const priceValid = priceText.trim() !== '' && Number.isFinite(price) && price > 0;
  const canSave = draft.name.trim() !== '' && priceValid;

  const handleSave = () => {
    if (!canSave) return;
    onSave({ ...draft, name: draft.name.trim(), priceAud: price });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-black/70 p-4 overflow-y-auto"
      data-testid="merch-item-modal"
      onClick={onCancel}
    >
      <div
        className="panel p-6 w-full max-w-md space-y-5 my-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between">
          <div>
            <p className="label-small-caps text-gold-bright">
              {mode === 'add' ? 'Add item' : 'Edit item'}
            </p>
            <h2 className="heading-display text-2xl text-cream mt-1">Merch &amp; aftercare</h2>
          </div>
          <button type="button" onClick={onCancel} className="btn-quiet" aria-label="Close">
            <X size={14} />
          </button>
        </header>

        <label className={labelCls}>
          Item name
          <input
            className={inputCls}
            value={draft.name}
            onChange={(e) => set('name', e.target.value)}
            data-testid="merch-item-name"
            autoFocus
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className={labelCls}>
            Category
            <select
              className={inputCls}
              value={draft.category}
              onChange={(e) => set('category', e.target.value as MerchItem['category'])}
              data-testid="merch-item-category"
            >
              <option value="aftercare">Aftercare</option>
              <option value="merch">Merch</option>
            </select>
          </label>
          <label className={labelCls}>
            Price (AUD)
            <input
              className={inputCls}
              type="number"
              min="0"
              step="1"
              inputMode="decimal"
              value={priceText}
              onChange={(e) => setPriceText(e.target.value)}
              data-testid="merch-item-price"
            />
          </label>
        </div>

        <label className={labelCls}>
          Status
          <select
            className={inputCls}
            value={draft.status}
            onChange={(e) => set('status', e.target.value as MerchItem['status'])}
            data-testid="merch-item-status"
          >
            <option value="active">Active (shown at checkout)</option>
            <option value="retired">Retired (hidden from checkout)</option>
          </select>
        </label>
        <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
          No commission on merch or aftercare. Retired items stay on past sales.
        </p>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="btn-quiet text-xs">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="merch-item-save"
          >
            {mode === 'add' ? 'Add item' : 'Save item'}
          </button>
        </div>
      </div>
    </div>
  );
}
